import React, { useEffect, useMemo, useState } from 'react';
import { Target, List, LayoutGrid } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader, EmptyState } from '@/components/common/Feedback';
import { QuickCaptureDialog } from '@/components/capture/QuickCaptureDialog';
import { OpportunityDrawer } from '@/components/opportunities/OpportunityDrawer';
import { StrengthBadge, ClassificationBadge, OppStatusBadge, DecisionBadge, AmountOrStatus } from '@/components/opportunities/OppBadges';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import { OPPORTUNITY_STATUS_META, fmtDate } from '@/lib/format';

const STATUS_FILTERS = [
  { value: 'open', label: 'Open' },
  { value: 'all', label: 'All' },
  { value: 'new', label: 'New' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'notice_sent', label: 'Notice Sent' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'approved', label: 'Approved' },
  { value: 'settled', label: 'Settled' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'dismissed', label: 'Dismissed' },
];

const OPEN_STATUSES = ['new', 'reviewed', 'notice_sent', 'submitted'];
// Columns shown in the tracker board (non-linear, left-to-right pipeline).
const BOARD_COLUMNS = ['new', 'reviewed', 'notice_sent', 'submitted', 'approved', 'settled'];

export default function Opportunities() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('open');
  const [view, setView] = useState('board');
  const [selected, setSelected] = useState(null);

  const load = () => {
    api.get('/opportunities').then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return items;
    if (statusFilter === 'open') return items.filter((d) => OPEN_STATUSES.includes(d.opportunity_status));
    return items.filter((d) => d.opportunity_status === statusFilter);
  }, [items, statusFilter]);

  const pricedCount = filtered.filter((d) => d.pricing_status === 'fully_priced').length;
  const byStatus = useMemo(() => {
    const g = {};
    BOARD_COLUMNS.forEach((s) => { g[s] = []; });
    items.forEach((o) => { if (g[o.opportunity_status]) g[o.opportunity_status].push(o); });
    return g;
  }, [items]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-500" data-testid="opportunities-subtitle">
          Everything worth chasing across your projects.
          {view === 'list' && filtered.length > 0 && <span className="ml-1 font-medium text-money-700">{pricedCount} fully priced.</span>}
        </p>
        <div className="flex items-center gap-2">
          {/* View toggle */}
          <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-white" data-testid="view-toggle">
            <button onClick={() => setView('board')} data-testid="view-board"
              className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors', view === 'board' ? 'bg-ink-900 text-white' : 'text-slate-500 hover:text-slate-800')}>
              <LayoutGrid className="h-3.5 w-3.5" />Board
            </button>
            <button onClick={() => setView('list')} data-testid="view-list"
              className={cn('flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors', view === 'list' ? 'bg-ink-900 text-white' : 'text-slate-500 hover:text-slate-800')}>
              <List className="h-3.5 w-3.5" />List
            </button>
          </div>
          {view === 'list' && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-9" data-testid="opp-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_FILTERS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <QuickCaptureDialog onDone={load} />
        </div>
      </div>

      {loading ? <Loader /> : items.length === 0 ? (
        <Card className="p-0"><EmptyState icon={Target} title="No opportunities yet"
          description="Use Add Anything to paste or upload a communication — Recoup will surface anything worth claiming." /></Card>
      ) : view === 'board' ? (
        <div className="overflow-x-auto pb-2">
          <div className="flex gap-4 min-w-max" data-testid="opp-board">
            {BOARD_COLUMNS.map((s) => {
              const col = byStatus[s] || [];
              const meta = OPPORTUNITY_STATUS_META[s];
              const colPriced = col.filter((o) => o.pricing_status === 'fully_priced').length;
              return (
                <div key={s} className="w-72 shrink-0" data-testid={`board-col-${s}`}>
                  <div className="flex items-center justify-between mb-2 px-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-ink-900">{meta.label}</span>
                      <span className="text-[11px] text-slate-400">{col.length}</span>
                    </div>
                    {colPriced > 0 && <span className="text-[11px] font-medium text-money-700">{colPriced} priced</span>}
                  </div>
                  <div className="space-y-2 rounded-xl bg-slate-50/70 p-2 min-h-[120px] ring-1 ring-slate-100">
                    {col.length === 0 && <div className="text-[11px] text-slate-400 text-center py-6">Nothing here</div>}
                    {col.map((o) => {
                      const isNew = o.opportunity_status === 'new';
                      return (
                      <button key={o.id} onClick={() => setSelected(o.id)} data-testid={`board-card-${o.id}`}
                        className={cn('w-full text-left rounded-lg bg-white ring-1 hover:ring-brand/40 hover:shadow-soft transition-all p-3', isNew ? 'ring-brand/40' : 'ring-slate-200')}>
                        <div className="flex items-start gap-1.5">
                          {isNew && <span className="mt-1 h-2 w-2 rounded-full bg-brand shrink-0" data-testid={`board-new-dot-${o.id}`} aria-label="New" />}
                          <div className={cn('text-sm line-clamp-2', isNew ? 'font-bold text-slate-900' : 'font-semibold text-slate-800')}>{o.title}</div>
                        </div>
                        <div className="text-[11px] text-slate-500 mt-0.5 truncate">{o.project_name}</div>
                        <div className="text-[10px] text-slate-400 mt-0.5">Detected: {fmtDate(o.created_at)}</div>
                        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                          <ClassificationBadge classification={o.classification} testid={`board-class-${o.id}`} />
                          <StrengthBadge strength={o.strength} confidence={o.confidence} />
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          {o.decision ? <DecisionBadge decision={o.decision} /> : <span />}
                          <AmountOrStatus opportunity={o} compact testid={`board-amount-${o.id}`} />
                        </div>
                      </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <Card className="p-0 overflow-hidden">
          {filtered.length === 0 ? (
            <EmptyState icon={Target} title="Nothing in this view" description="Try a different status filter." />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAFBFC] text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2.5">Opportunity</th>
                    <th className="text-left px-4 py-2.5 hidden md:table-cell">Project</th>
                    <th className="text-left px-4 py-2.5">Classification</th>
                    <th className="text-left px-4 py-2.5">Strength</th>
                    <th className="text-right px-4 py-2.5">Pricing</th>
                    <th className="text-center px-4 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((d) => {
                    const isNew = d.opportunity_status === 'new';
                    return (
                    <tr key={d.id} className={cn('row-hover cursor-pointer', isNew && 'bg-brand-50/40')} onClick={() => setSelected(d.id)} data-testid={`opp-row-${d.id}`}>
                      <td className="px-4 py-3 max-w-sm">
                        <div className="flex items-start gap-2">
                          <span className={cn('mt-1.5 h-2 w-2 rounded-full shrink-0', isNew ? 'bg-brand' : 'bg-transparent')} data-testid={isNew ? `opp-new-dot-${d.id}` : undefined} aria-label={isNew ? 'New' : undefined} />
                          <div className="min-w-0">
                            <div className={cn('text-slate-800', isNew ? 'font-bold' : 'font-semibold')}>{d.title}</div>
                            <div className={cn('text-xs line-clamp-1', isNew ? 'text-slate-600' : 'text-slate-500')}>{d.summary}</div>
                            <div className="text-[11px] text-slate-400 mt-0.5" data-testid={`opp-detected-${d.id}`}>Detected: {fmtDate(d.created_at)}</div>
                          </div>
                        </div>
                      </td>
                      <td className={cn('px-4 py-3 hidden md:table-cell', isNew ? 'text-slate-700 font-medium' : 'text-slate-600')}>{d.project_name}</td>
                      <td className="px-4 py-3"><ClassificationBadge classification={d.classification} testid={`opp-class-${d.id}`} /></td>
                      <td className="px-4 py-3"><StrengthBadge strength={d.strength} confidence={d.confidence} /></td>
                      <td className="px-4 py-3 text-right"><AmountOrStatus opportunity={d} testid={`opp-amount-${d.id}`} /></td>
                      <td className="px-4 py-3 text-center"><OppStatusBadge status={d.opportunity_status} /></td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <OpportunityDrawer opportunityId={selected} open={!!selected} onClose={() => setSelected(null)} onChanged={load} />
    </div>
  );
}
